import { Dictionary, VueRouter } from "vue-router/types/router";

import { LogService, SnackbarService } from "@/services";
import { useSearchStore } from "@/pinia/search";
import {
  LookupTableItemKeys,
  LookupTablesKeys,
  NestedLookupTableItem,
  ProfileView,
  SearchHelpers,
  SearchOrder,
  SearchQuery,
  SearchQueryTableItemKeys,
  VignetteRoutes,
} from "@/types";
import { useAppStore } from "@/pinia/app";
import {
  AllSearchParamsKeys,
  CongressionalSearchParams,
  FederalSearchParams,
  StateSearchParams,
  TypedURLSearchParams,
  UIOnlyParams,
  UniversalSearchParams,
} from "@/types/api/SearchEndpointInputs";

/**
 * This search service is essentially the implementation of the TypedURLSearchParams class.
 */
export class SearchService {
  private static _initialized = false;
  private static appStore: ReturnType<typeof useAppStore>;
  private static searchStore: ReturnType<typeof useSearchStore>;

  static _initialize(): void {
    if (this._initialized) {
      LogService.error("SearchService is already initialized.");
      return;
    }
    LogService.debug("Initializing SearchService.");

    this.appStore = useAppStore();
    this.searchStore = useSearchStore();

    this._initialized = true;
    LogService.info("SearchService is initialized.");
  }

  /**
   * Check to see if the user entered parameters have changed since the last search.
   * This is do-able via string comparison as the TypedURLSearchParams toString() method sorts the params alphabetically
   * to ensure that the result each time toString() is called is the same.
   * @param currentSearchQuery
   */
  static isSearchQueryChanged(currentSearchQuery: SearchQuery): boolean {
    const lastQuery: string = this.searchStore.lastSearchString;
    const currentQuery: string =
      this.generateQuery(currentSearchQuery).toString();
    return lastQuery !== currentQuery;
  }

  /**
   * Checks if the search query is empty. If extraChecks are provided, they will be run before checking if the query is empty.
   * @param currentSearchQuery - the current search query
   * @param extraIsEmptyChecks these optional checks are for special cases where the default value of the query is a truthy value
   * see "office" on the congressional endpoint for an example of this. "Senate", "House", and "Governor" are all checked by default
   * if isEmpty it will return the fields to ignore from the searchParams
   * @returns boolean - true if the query is empty, false otherwise
   */
  static isSearchQueryEmpty(
    currentSearchQuery: SearchQuery,
    extraIsEmptyChecks: (() => AllSearchParamsKeys | null)[] = []
  ): boolean {
    const searchParams = this.generateQuery(currentSearchQuery);
    extraIsEmptyChecks.forEach((check) => {
      // keep checking until one of the isEmptyChecks is false then return false for query not being empty
      const field = check();
      field && searchParams.delete(field);
    });
    // otherwise return true if the query is empty
    return searchParams.isEmpty();
  }

  static readSearchQueriesFromRoute(): SearchQuery {
    const params = new TypedURLSearchParams(window.location.search);

    // get sig values for use below
    const { sigId, operator, rating } = SearchHelpers.parseSigExpressionString(
      params.get("sig_expression") || ""
    );
    const sig = sigId
      ? this.appStore.getLookupTableEntryByType(
          LookupTablesKeys.SPECIAL_INTEREST_GROUPS_FLAT,
          LookupTableItemKeys.ID,
          sigId
        )
      : null;
    const searchQuery: SearchQuery = {
      // Search configuration
      page: Number(params.get("page")) || 1,
      pageSize: Number(params.get("pageSize")) || 12,
      order: (params.get("order") as SearchOrder) || SearchOrder.DEFAULT,
      view: params.getUIParam("view") || ProfileView.DEFAULT,
      hidePayWalledProfiles: params.getBoolean("hide_paywalled_profiles"),
      // Local variables
      autoCompleteKeyword: "",

      // Vignette simple search
      keyword: params.get("q") || "",

      // Vignette advanced search
      currentOfficeCongressionalDistrict:
        this.appStore.getLookupTableEntryByType(
          LookupTablesKeys.STATES,
          LookupTableItemKeys.ID,
          params.get("current_office_congressional_district") || ""
        ),
      currentOfficeStates: params.getAll("current_office_state_abbr"),
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      politicalParty: params.get("political_party") || "",
      religion: this.appStore.getLookupTableEntryByType(
        LookupTablesKeys.RELIGIONS,
        LookupTableItemKeys.NAME,
        params.get("religion") || ""
      ),
      ethnicity: this.appStore.getLookupTableEntryByType(
        LookupTablesKeys.ETHNICITIES,
        LookupTableItemKeys.NAME,
        params.get("ethnicity") || ""
      ),
      gender: params.get("gender") || "",
      state: this.appStore.getLookupTableEntryByType(
        LookupTablesKeys.STATES,
        LookupTableItemKeys.SHORT_CODE,
        params.get("state") || ""
      ),
      country: this.appStore.getLookupTableEntryByType(
        LookupTablesKeys.COUNTRIES,
        LookupTableItemKeys.NAME,
        params.get("country") || ""
      ),
      editorialGroup: this.appStore.getLookupTableEntryByType(
        LookupTablesKeys.EDITORIAL_GROUPS,
        LookupTableItemKeys.ID,
        params.get("editorial_groups") || ""
      ),
      profileTypes: params
        .getAll("profile_types")
        .map((entryId) =>
          this.appStore.getLookupTableEntryByType(
            LookupTablesKeys.PROFILE_TYPES_FLAT,
            LookupTableItemKeys.ID,
            entryId
          )
        ),
      keyPolicyAreas: params
        .getAll("key_policy_areas")
        .map((entryId) =>
          this.appStore.getLookupTableEntryByType(
            LookupTablesKeys.KEY_POLICY_AREAS_FLAT,
            LookupTableItemKeys.ID,
            entryId
          )
        ),
      industries: params
        .getAll("industry")
        .map((entryId) =>
          this.appStore.getLookupTableEntryByType(
            LookupTablesKeys.INDUSTRIES_FLAT,
            LookupTableItemKeys.ID,
            entryId
          )
        ),
      committees: params
        .getAll("committees")
        .map((entryId) =>
          this.appStore.getLookupTableEntryByType(
            LookupTablesKeys.COMMITTEES_FLAT,
            LookupTableItemKeys.ID,
            entryId
          )
        ),
      committee: params
        .getAll("committee")
        .map((entryId) =>
          this.appStore.getLookupTableEntryByType(
            LookupTablesKeys.COMMITTEES_FLAT,
            LookupTableItemKeys.ID,
            entryId
          )
        ),
      school: params.get("school") || "",
      caucuses: [],
      agencies: params.getAll("agencies"),
      city: params.get("city") || "",
      education: params.get("education") || "",
      includeFormer: params.getBoolean("include_former"),
      // Birthdate
      birthYear: { id: "", name: "" },
      age: { id: "", name: "" },
      birthDateOperator: { id: "exact", name: "Born On" },
      // Special Interest Groups
      sig: sig,
      sigOperator: this.appStore.getLookupTableEntryByType(
        LookupTablesKeys.SIG_OPERATOR,
        LookupTableItemKeys.ID,
        operator
      ),
      sigRating: this.appStore.getLookupTableEntryByType(
        LookupTablesKeys.SIG_RATING_OPTIONS,
        LookupTableItemKeys.ID,
        rating
      ),

      // congressional/federal
      appointment: params.getAll("appointment"),
      appointment2: params.get("appointment2") || "",
      caucus: params
        .getAll("caucus")
        .map((entryId) =>
          this.appStore.getLookupTableEntryByType(
            LookupTablesKeys.CAUCUSES_FLAT,
            LookupTableItemKeys.ID,
            entryId
          )
        ),
      degree: params.get("degree") || "",
      district: params.getAll("district") || [],
      interest: params.getAll("interest") || [],
      interest2: [],
      jobtitle: [],
      jobtitle2: params.getAll("jobtitle2"),
      jobtitle2_option: params.get("jobtitle2_option") || "",
      majmin: params.get("majmin") || "",
      office_location: params.getAll("office_location") || "",
      offset: params.get("offset") || "",
      party: params.get("party") || "",
      topagency2: params.get("topagency2") || "",
      topagency: params.get("topagency") || "",
      type: params.get("type") || "",
      // congressional search
      office: [],
      // federal search
      agency: params.getAll("agency"),
    };

    if (params.getAll("profile_types").length > 0) {
      searchQuery.profileTypes = params
        .getAll("profile_types")
        .map((entryId) =>
          this.appStore.getLookupTableEntryByType(
            LookupTablesKeys.PROFILE_TYPES_FLAT,
            LookupTableItemKeys.ID,
            entryId
          )
        );
    }
    if (params.getAll("key_policy_areas").length > 0) {
      searchQuery.keyPolicyAreas = params
        .getAll("key_policy_areas")
        .map((entryId) =>
          this.appStore.getLookupTableEntryByType(
            LookupTablesKeys.KEY_POLICY_AREAS_FLAT,
            LookupTableItemKeys.ID,
            entryId
          )
        );
    }
    if (params.getAll("industry").length > 0) {
      searchQuery.industries = params
        .getAll("industry")
        .map((entryId) =>
          this.appStore.getLookupTableEntryByType(
            LookupTablesKeys.INDUSTRIES_FLAT,
            LookupTableItemKeys.ID,
            entryId
          )
        );
    }
    if (params.getAll("committees").length > 0) {
      searchQuery.committees = params
        .getAll("committees")
        .map((entryId) =>
          this.appStore.getLookupTableEntryByType(
            LookupTablesKeys.COMMITTEES_FLAT,
            LookupTableItemKeys.ID,
            entryId
          )
        );
    }
    if (params.getAll("caucuses").length > 0) {
      searchQuery.caucuses = params
        .getAll("caucuses")
        .map((entryId) =>
          this.appStore.getLookupTableEntryByType(
            LookupTablesKeys.CAUCUSES_FLAT,
            LookupTableItemKeys.ID,
            entryId
          )
        );
    }

    // Initialize Birthdate - either birth year or age
    const birthYearParam = params.get("birth_year");
    const ageParam = params.get("age");
    if (birthYearParam) {
      const { value: birthYear, operator: birthYearOperator } =
        SearchHelpers.parseBirthYearString(birthYearParam);
      searchQuery.birthYear = this.appStore.getLookupTableEntryByType(
        LookupTablesKeys.BIRTH_YEAR_OPTIONS,
        LookupTableItemKeys.ID,
        birthYear
      );
      searchQuery.birthDateOperator = this.appStore.getLookupTableEntryByType(
        LookupTablesKeys.BIRTH_YEAR_OPERATOR,
        LookupTableItemKeys.ID,
        birthYearOperator
      );
    } else if (ageParam) {
      const { value: age, operator: ageOperator } =
        SearchHelpers.parseBirthYearString(ageParam);
      searchQuery.age = this.appStore.getLookupTableEntryByType(
        LookupTablesKeys.AGE_OPTIONS,
        LookupTableItemKeys.ID,
        age
      );
      searchQuery.birthDateOperator = this.appStore.getLookupTableEntryByType(
        LookupTablesKeys.AGE_OPERATOR,
        LookupTableItemKeys.ID,
        ageOperator
      );
    }

    // congressional/federal advanced stuff
    const caucus = params.getAll("caucus");
    if (caucus.length > 0) {
      searchQuery.caucus = caucus.map((entryId) =>
        this.appStore.getLookupTableEntryByType(
          LookupTablesKeys.CAUCUSES_FLAT,
          LookupTableItemKeys.ID,
          entryId
        )
      );
    }
    const interest2 = params.getAll("interest2");
    if (interest2.length > 0) {
      searchQuery.interest2 = interest2.map((entryId) =>
        this.appStore.getLookupTableEntryByType(
          LookupTablesKeys.SPECIAL_INTEREST_GROUPS_FLAT,
          LookupTableItemKeys.ID,
          entryId
        )
      );
    }
    const jobtitle = params.getAll("jobtitle");
    if (jobtitle.length > 0) {
      searchQuery.jobtitle = jobtitle.map(
        (entryId) =>
          this.appStore.getLookupTableEntryByType(
            LookupTablesKeys.JOB_CATEGORIES,
            LookupTableItemKeys.NAME,
            entryId
          ).name
      );
    }
    const office = params.getAll("office");
    if (office.length > 0) {
      searchQuery.office = office;
    }

    return searchQuery;
  }

  /**
   * This function contains all the logic necessary to build a query. It is a little complex but it is
   * dynamic in that as long as you follow the steps outlined in "SearchEndpointInputs.ts", you should only have to
   * edit setup variables. The logic in this function should not have to change unless something drastic changes.
   * @param searchQuery
   */
  static generateQuery(
    searchQuery: SearchQuery = this.searchStore.getCurrentSearchQuery
  ): TypedURLSearchParams {
    // <editor-fold desc="Setup Variables and Logic Functions">

    // A variable to hold the params, this is used in the first step to get the values into strings.
    const _routeQuery: Dictionary<string | string[]> = {};

    interface SpecialCases {
      [key: string]: (...params: any) => string;
    }

    /**
     * This object holds all the special cases that need to be handled differently than the rest of the params.
     * Mostly due to quirks in the way the params are passed to the api or parameters that built from multiple from
     * the SearchQuery object.
     */
    const specialCases: SpecialCases = {
      birthYear: (query: SearchQuery) => {
        const year = query.birthYear.id;
        const op = query.birthDateOperator.id;
        return year ? SearchHelpers.getBirthYearString(year, "", op) : "";
      },
      age: (query: SearchQuery) => {
        const age = query.age.id;
        const op = query.birthDateOperator.id;
        return age ? SearchHelpers.getBirthYearString("", age, op) : "";
      },
      sig: (query: SearchQuery) => {
        const id = query?.sig?.id ?? "";
        const op = query.sigOperator.id;
        const rating = query.sigRating.id;
        return id ? SearchHelpers.getSigExpressionString(id, op, rating) : "";
      },
    };

    /**
     * Fields that are not required for the API query
     */
    type Ommitables =
      // | "view"
      "birthDateOperator" | "sigOperator" | "sigRating";
    type SearchQueryWithOmmitables = Omit<SearchQuery, Ommitables>;
    type UIToApiMapper = {
      [key in keyof SearchQueryWithOmmitables]:
        | "view"
        | keyof UniversalSearchParams
        | keyof CongressionalSearchParams
        | keyof StateSearchParams
        | keyof FederalSearchParams;
    };

    /**
     * This object holds the mapping between how they are named in the UI and how they are named in the API.
     * This is part of Step 6 of Start Search in SearchEndpointInputs.ts
     */
    const mapping: UIToApiMapper = {
      view: "view",
      age: "age",
      agency: "agency",
      agencies: "agencies",
      appointment: "appointment",
      appointment2: "appointment2",
      autoCompleteKeyword: "autocomplete",
      birthYear: "birth_year",
      caucus: "caucus",
      caucuses: "caucuses",
      city: "city",
      committee: "committee",
      committees: "committees",
      country: "country",
      currentOfficeCongressionalDistrict:
        "current_office_congressional_district",
      currentOfficeStates: "current_office_state_abbr",
      degree: "degree",
      district: "district",
      editorialGroup: "editorial_groups",
      education: "education",
      ethnicity: "ethnicity",
      gender: "gender",
      hidePayWalledProfiles: "hide_paywalled_profiles",
      includeFormer: "include_former",
      industries: "industry",
      interest2: "interest2",
      interest: "interest",
      jobtitle2: "jobtitle2",
      jobtitle2_option: "jobtitle2_option",
      jobtitle: "jobtitle",
      keyPolicyAreas: "key_policy_areas",
      keyword: "q",
      majmin: "majmin",
      office: "office",
      office_location: "office_location",
      offset: "offset",
      order: "order",
      page: "page",
      pageSize: "pageSize",
      party: "party",
      politicalParty: "political_party",
      profileTypes: "profile_types",
      religion: "religion",
      school: "school",
      sig: "sig_expression",
      state: "state",
      topagency2: "topagency2",
      topagency: "topagency",
      type: "type",
    };

    /**
     * This type is used to provide type safety in grabbing the correct fields for searching the API with
     * specific filters. This is needed because we don't currently use the same key for all searches
     * from all filter objects. For example, if we always filtered by the "id" of an item this would not be needed.
     * So if you are adding a new search parameters and what is stored in SearchQuery is an object, you need to add
     * the field here and then what key holds the value to be sent to the api.
     */
    type TableItemToSearchFieldMap = {
      [K in SearchQueryTableItemKeys]: keyof Omit<
        NestedLookupTableItem,
        "children"
      >;
    };
    const tableItemToSearchFieldMap: Omit<
      TableItemToSearchFieldMap,
      Ommitables | "age" | "birthYear" | "sig"
    > = {
      caucus: "id",
      caucuses: "id",
      committee: "id",
      committees: "id",
      country: "name",
      currentOfficeCongressionalDistrict: "id",
      editorialGroup: "id",
      ethnicity: "name",
      industries: "id",
      interest2: "id",
      keyPolicyAreas: "id",
      profileTypes: "id",
      religion: "name",
      state: "short_code",
    };

    // helper functions (and type guards) to make reading if statements easier
    const isObject = (v: unknown) =>
      typeof v === "object" && v !== null && !Array.isArray(v);
    const isNotEmptyArray = (v: unknown): v is unknown[] =>
      Array.isArray(v) && v.length > 0;
    const isTruthyPrimitive = (v: unknown): v is true | string | number =>
      !!v && !isObject(v) && !Array.isArray(v);
    const isObjectAndHasValidId = (
      v: unknown
    ): v is Omit<NestedLookupTableItem, "children"> =>
      isObject(v) && !!(v as NestedLookupTableItem)?.id;

    // this is what does the value grabbing and checking for object keys from tableItemToSearchFieldMap
    const handleUnknownValue = (
      v: unknown,
      key: keyof typeof tableItemToSearchFieldMap
    ): string | string[] => {
      if (isObjectAndHasValidId(v)) {
        const mappedKey = tableItemToSearchFieldMap[key];
        return v[mappedKey] || "";
      }
      if (isNotEmptyArray(v)) {
        return v.map((e) => handleUnknownValue(e, key)) as string[]; // won't be nested
      }
      return v + "";
    };

    /**
     * This is where most of the messy magic happens. It cleans the value from SearchQuery, checks tos ee if it's a special
     * case, and then maps the value to the correct field from the mapping object.
     * @param key
     */
    const cleanQueryKeyForSearch = (
      key: string
    ): {
      key: AllSearchParamsKeys | keyof UIOnlyParams;
      value: string | string[];
    } => {
      let returnValue: string | string[] = "";
      const value = _query[key as keyof SearchQuery];
      const isSpecialCase = specialCases.hasOwnProperty(key);
      const mappedKey: AllSearchParamsKeys | keyof UIOnlyParams =
        mapping[key as keyof typeof mapping];
      if (
        (isNotEmptyArray(value) ||
          isObjectAndHasValidId(value) ||
          isTruthyPrimitive(value)) &&
        mappedKey !== undefined
      ) {
        if (isSpecialCase) {
          const specialValue = specialCases[key](_query);
          if (specialValue) {
            returnValue = specialValue;
          }
        } else {
          returnValue = handleUnknownValue(
            value,
            key as keyof typeof tableItemToSearchFieldMap
          );
        }
      }
      return { key: mappedKey, value: returnValue };
    };

    // </editor-fold> end setup logic/variables

    const _query = searchQuery;
    // map UI Query fields to API Query fields and put them into string format
    for (const key of Object.keys(_query)) {
      const { key: mappedKey, value } = cleanQueryKeyForSearch(key);
      if (!!value) {
        _routeQuery[mappedKey] = value;
      }
    }

    const searchParams = new TypedURLSearchParams("");

    // put the cleaned values into the searchParams object
    for (const key of Object.keys(_routeQuery)) {
      const k = key as AllSearchParamsKeys;
      const value = _routeQuery[k];
      if (Array.isArray(value)) {
        value.forEach((v) => searchParams.append(k, v));
      } else {
        searchParams.append(k, value);
      }
    }
    return searchParams;
  }

  // When last query is used, most likely means search was changed using search chips
  static search(
    $router: VueRouter,
    useLastQuery = false,
    toRoute: VignetteRoutes | "" = ""
  ): void {
    let currentSearchQuery: SearchQuery =
      this.searchStore.getCurrentSearchQuery;

    if (useLastQuery) {
      const lastSearchQuery: SearchQuery | null =
        this.searchStore.lastSearchQuery;
      if (lastSearchQuery == null) {
        LogService.warn(
          "SearchService.search: called with useLastQuery flag and null lastSearchQuery."
        );
        return;
      }
      // We can just proceed with the search, assuming we just removed a filter on SearchResults page and page is valid
      currentSearchQuery = lastSearchQuery;
    } else {
      currentSearchQuery.page = 1;
    }

    // Ready to search
    this._search($router, currentSearchQuery, toRoute);
  }

  static validateSearchQuery(
    searchQuery: SearchQuery,
    extraIsEmptyChecks: (() => AllSearchParamsKeys | null)[] = []
  ): boolean {
    if (SearchService.isSearchQueryEmpty(searchQuery, extraIsEmptyChecks)) {
      SnackbarService.displaySnackbar(
        "Please enter a search keyword or select your advanced search criteria."
      );
    } else if (SearchService.isSearchQueryChanged(searchQuery)) {
      return true;
    } else {
      SnackbarService.displaySnackbar("Please alter your search criteria.");
    }
    return false;
  }

  private static async _search(
    $router: VueRouter,
    searchQuery: SearchQuery,
    toRoute: VignetteRoutes | "" = ""
  ): Promise<void> {
    const $route = $router.currentRoute;
    const routeQueryString = this.generateQuery(searchQuery).toString();
    let route = $route.path;
    if (toRoute) route = $router.resolve({ name: toRoute }).href;
    else if (!$route.fullPath.includes("search")) {
      route = "/search/congressional";
    }
    route = route + "?" + routeQueryString;
    $router.push(route).catch(() => {
      return;
    });
  }

  static clearSearchQuery() {
    const searchStore = useSearchStore();
    searchStore.resetSearch();
    searchStore.$patch({ searchFormIsHidden: false });
  }
}
